/**
 * Infergraph MUI Theme
 *
 * VC-G7 — design-system/tokens.ts 의 token 을 MUI theme 으로 변환.
 * light/dark dual theme 지원 (VC-G4 prereq).
 */
import { createTheme, type ThemeOptions } from '@mui/material/styles'
import {
  palette,
  fontFamily,
  typography,
  radius,
  shadow,
  motion,
  tokensForMode,
  type ColorMode,
} from './tokens'

const muiTypography = {
  fontFamily: fontFamily.sans,
  h1: {
    fontSize: typography.display1.size,
    lineHeight: `${typography.display1.lineHeight}px`,
    fontWeight: typography.display1.weight,
    letterSpacing: `${typography.display1.letterSpacing}em`,
  },
  h2: {
    fontSize: typography.display2.size,
    lineHeight: `${typography.display2.lineHeight}px`,
    fontWeight: typography.display2.weight,
    letterSpacing: `${typography.display2.letterSpacing}em`,
  },
  h3: {
    fontSize: typography.heading1.size,
    lineHeight: `${typography.heading1.lineHeight}px`,
    fontWeight: typography.heading1.weight,
  },
  h4: {
    fontSize: typography.heading2.size,
    lineHeight: `${typography.heading2.lineHeight}px`,
    fontWeight: typography.heading2.weight,
  },
  h5: {
    fontSize: typography.heading3.size,
    lineHeight: `${typography.heading3.lineHeight}px`,
    fontWeight: typography.heading3.weight,
  },
  h6: {
    fontSize: typography.heading4.size,
    lineHeight: `${typography.heading4.lineHeight}px`,
    fontWeight: typography.heading4.weight,
  },
  body1: {
    fontSize: typography.body1.size,
    lineHeight: `${typography.body1.lineHeight}px`,
  },
  body2: {
    fontSize: typography.body2.size,
    lineHeight: `${typography.body2.lineHeight}px`,
  },
  caption: {
    fontSize: typography.caption.size,
    lineHeight: `${typography.caption.lineHeight}px`,
    letterSpacing: `${typography.caption.letterSpacing}em`,
  },
} as const

const buildShape = () => ({
  borderRadius: radius.md,
})

const buildTransitions = () => ({
  duration: {
    shortest: motion.duration.instant,
    shorter: motion.duration.fast,
    short: motion.duration.normal,
    standard: motion.duration.normal,
    complex: motion.duration.slow,
    enteringScreen: motion.duration.normal,
    leavingScreen: motion.duration.fast,
  },
  easing: {
    easeInOut: motion.easing.default,
    easeOut: motion.easing.decel,
    easeIn: motion.easing.accel,
    sharp: motion.easing.default,
  },
})

const buildPalette = (mode: ColorMode) => {
  const m = tokensForMode(mode)
  return {
    mode,
    primary: {
      light: palette.primary[100],
      main: palette.primary[500],
      dark: palette.primary[700],
      contrastText: palette.neutral.light[0],
    },
    secondary: {
      main: palette.accent.graph,
      contrastText: palette.neutral.light[0],
    },
    success: { main: m.semantic.success },
    warning: { main: m.semantic.warning },
    error: { main: m.semantic.error },
    info: { main: m.semantic.info },
    background: {
      // R7 — 표면 위계: 캔버스는 옅은 회색, 카드(paper)는 흰색. 캔버스와
      // 카드가 같은 순백이면 위계가 0 이라 "흰 바탕에 종이 쪼가리"가 된다.
      default: m.surface.canvas,
      paper: m.surface.card,
    },
    text: {
      primary: m.neutral[900],
      secondary: m.neutral[700],
      disabled: m.neutral[500],
    },
    divider: m.neutral[300],
  }
}

export const buildTheme = (mode: ColorMode = 'light') => {
  const options: ThemeOptions = {
    palette: buildPalette(mode),
    typography: muiTypography,
    shape: buildShape(),
    transitions: buildTransitions(),
    shadows: [
      shadow.none,
      shadow.sm,
      shadow.sm,
      shadow.md,
      shadow.md,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
      shadow.xl,
    ] as ThemeOptions['shadows'],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            boxShadow: shadow.md,
          },
        },
      },
    },
  }
  return createTheme(options)
}

export const lightTheme = buildTheme('light')
export const darkTheme = buildTheme('dark')
